import { useEffect, useId, useState } from 'react';
import type { Filtros as TFiltros } from '../api';
import { mesCapital, nombreTipoPlural } from '../formato';

interface Props {
  valor: TFiltros;
  anios: number[];
  tipos: string[];
  onAplicar: (f: TFiltros) => void;
}

const MESES_NUM = Array.from({ length: 12 }, (_, i) => i + 1);

export function Filtros({ valor, anios, tipos, onAplicar }: Props) {
  const id = useId();
  const [q, setQ] = useState(valor.q ?? '');
  const [numero, setNumero] = useState(valor.numero ? String(valor.numero) : '');
  // En pantallas chicas los filtros secundarios se pliegan para que los resultados queden a la vista.
  const [abiertos, setAbiertos] = useState(false);
  const activos = [valor.tipo, valor.anio, valor.mes, valor.numero].filter(Boolean).length;

  // Si la URL cambia (atrás/adelante), los campos de texto se sincronizan.
  useEffect(() => setQ(valor.q ?? ''), [valor.q]);
  useEffect(() => setNumero(valor.numero ? String(valor.numero) : ''), [valor.numero]);

  const aplicar = (cambios: Partial<TFiltros>) => onAplicar({ ...valor, ...cambios, pagina: undefined });

  const enviar = (e: React.FormEvent) => {
    e.preventDefault();
    const n = Number(numero.replace(/\D/g, ''));
    aplicar({ q: q.trim() || undefined, numero: n > 0 ? n : undefined });
  };

  const hayFiltros = Boolean(
    valor.anio || valor.mes || valor.tipo || valor.numero || valor.q || valor.exacto,
  );

  return (
    <form className="filtros" onSubmit={enviar} role="search" aria-label="Buscar normas">
      <div className="campo campo--texto">
        <label htmlFor={`${id}-q`}>Palabras en el texto</label>
        <div className="buscador">
          <input
            id={`${id}-q`}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="p. ej. habilitación comercial"
            maxLength={255}
            autoComplete="off"
          />
          <button type="submit" className="boton boton--primario">
            Buscar
          </button>
        </div>
        <label className="casilla">
          <input
            type="checkbox"
            checked={Boolean(valor.exacto)}
            onChange={(e) => aplicar({ q: q.trim() || undefined, exacto: e.target.checked || undefined })}
          />
          Buscar la frase exacta
        </label>
      </div>

      <button
        type="button"
        className="enlace filtros__plegar"
        aria-expanded={abiertos}
        aria-controls={`${id}-grilla`}
        onClick={() => setAbiertos((a) => !a)}
      >
        {abiertos ? 'Ocultar filtros' : 'Filtrar por tipo, año, mes o número'}
        {!abiertos && activos > 0 && ` (${activos} en uso)`}
      </button>

      <div className="filtros__grilla" id={`${id}-grilla`} data-abierto={abiertos}>
        <div className="campo">
          <label htmlFor={`${id}-tipo`}>Tipo de norma</label>
          <select
            id={`${id}-tipo`}
            value={valor.tipo ?? ''}
            onChange={(e) => aplicar({ tipo: e.target.value || undefined })}
          >
            <option value="">Todas</option>
            {tipos.map((t) => (
              <option key={t} value={t}>
                {nombreTipoPlural(t)}
              </option>
            ))}
          </select>
        </div>

        <div className="campo">
          <label htmlFor={`${id}-anio`}>Año</label>
          <select
            id={`${id}-anio`}
            value={valor.anio ?? ''}
            onChange={(e) => aplicar({ anio: Number(e.target.value) || undefined })}
          >
            <option value="">Todos</option>
            {anios.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>

        <div className="campo">
          <label htmlFor={`${id}-mes`}>Mes</label>
          <select
            id={`${id}-mes`}
            value={valor.mes ?? ''}
            onChange={(e) => aplicar({ mes: Number(e.target.value) || undefined })}
          >
            <option value="">Todos</option>
            {MESES_NUM.map((m) => (
              <option key={m} value={m}>
                {mesCapital(m)}
              </option>
            ))}
          </select>
        </div>

        <div className="campo">
          <label htmlFor={`${id}-numero`}>Número</label>
          <input
            id={`${id}-numero`}
            inputMode="numeric"
            value={numero}
            onChange={(e) => setNumero(e.target.value.replace(/\D/g, '').slice(0, 9))}
            onBlur={() => {
              const n = Number(numero) || undefined;
              if (n !== valor.numero) aplicar({ numero: n });
            }}
            placeholder="Cualquiera"
          />
        </div>
      </div>

      {hayFiltros && (
        <button type="button" className="enlace filtros__limpiar" onClick={() => onAplicar({})}>
          Quitar filtros
        </button>
      )}
    </form>
  );
}
